import {
  IconHome,
  IconCommand,
  IconJourney,
  IconPricing,
  IconReviews,
  IconArtwork,
  IconBuilders,
  IconPresent,
  IconTopTier,
  IconTeam,
  IconSpark,
  IconCheck,
} from './components/icons.jsx'
import { ACTIONS } from './lib/rbac.js'
import { INTERNAL_LABEL, INTERNAL_ROUTE, EXTERNAL_LABEL } from './lib/surfaces.js'

/**
 * Primary navigation. Order and labels match the live hub sidebar
 * (Home, Command Center, Customer Journey, Pricing Engine, Artwork Studio,
 * Product Builders), then extend it as a real, routed platform.
 *
 * `status` drives the small state tag in the sidebar:
 *   'live'  — fully built
 *   'beta'  — usable, still expanding
 *   'soon'  — routed placeholder
 *
 * `action` is the RBAC view permission for the module. The Sidebar runs the
 * list through `visibleNav()`, so a role that cannot view a module never sees
 * its link. Entries with no `action` (Home) are open to every session. This is
 * navigation hygiene, NOT access control — the route still renders if typed,
 * and the backend is what must refuse the data. See RBAC.md.
 */
/**
 * ⚠️ EVERY ENTRY SAYS WHAT IT IS FOR, WHO IT IS FOR, AND WHAT IT READS.
 *
 * Brian: "On all of our systems, my team will ask what each of them are for.
 * That should be in the Home Screen."
 *
 * These three fields are declared HERE, on the same registry the sidebar reads,
 * rather than written out again on the Home page. A directory that is typed
 * separately drifts from the nav the first time somebody adds a module, and
 * then the page explaining the system is the thing that is wrong about it.
 *
 *   purpose   one line — what job it does
 *   audience  'SR' (internal) or 'Customer' (somebody outside SR signs in)
 *   reads     the SOURCE OF TRUTH behind it, so two modules showing the same
 *             fact can be told apart by where the fact came from
 *
 * `audience` is the field that answers the question actually being asked.
 *
 * ⚠️ CORRECTED 2026-09-11. This comment used to say the difference between
 * Production and Team Meeting Rooms was "one is SR looking at every order and
 * the other is one coach looking at their own". **That was measured and it is
 * wrong** — the Team Meeting Room's staff console is an SR-facing app with
 * roughly twenty panels, so both are surfaces SR staff open daily, and audience
 * as stated could not tell them apart.
 *
 * Brian's rule is the real one: **INTERNAL vs EXTERNAL.** The Room is SR's one
 * CUSTOMER-VISIBLE surface — approvals, signatures, rosters, the review ask,
 * the contract. Production is the floor, and no customer ever sees it. External
 * means customer-visible, NOT staff-free.
 *
 * The full statement lives in lib/surfaces.js and is rendered on both pages,
 * so it cannot drift from what this comment claims.
 */
export const NAV = [
  { to:'/performance', label:'Performance', icon:IconCheck, status:'live', action:ACTIONS.CC_VIEW, purpose:'Measure coverage, account progress, artwork revisions and outcomes with a defined cohort.', audience:'SR', reads:'Authorized workspace records, championship feed and order feed' },
  { to: '/', label: 'Home', icon: IconHome, end: true, status: 'live',
    purpose: 'The front door — today’s numbers and what needs attention.',
    audience: 'SR', reads: `Command Center + ${INTERNAL_LABEL}` },
  /* MY WORKLIST — SECOND, FOR ALL NINE REPS, AND NOT THE LANDING.
   *
   * ⚠️ THE ROUTE SHIPPED IN 2.88.0 WITH NOTHING LINKING TO IT. `/my-worklist`
   * was reachable only by typing the URL, so the screen built to tell a rep what
   * to do today could not be found by the people it was built for. One registry
   * entry is the whole fix; the page itself is unchanged.
   *
   * ⚠️ BRIAN RULED ALL NINE REPS, NOT THE PILOT THREE. So this is gated on
   * CC_VIEW — the same action as the Command Center, granted to every role — and
   * NOT on a hand-kept allowlist of Zach Muller, Philip Davanzo III and Faith
   * Curtis. An allowlist of names in a nav file is a roster that drifts from the
   * real one the first time someone is hired, and the page already answers an
   * identity with no book in its own `unmapped` voice rather than showing it an
   * empty book. Visibility here is navigation hygiene; the API's scope block is
   * what decides whose records come back.
   *
   * ⚠️ STILL NOT THE DEFAULT LANDING. `/` stays Home. Linking a route is
   * reversible; moving the front door is a separate, deliberate flip, and
   * MyWorklist.jsx's own header comment says the pilot decides it.
   */
  {
    to: '/my-worklist',
    label: 'My Worklist',
    icon: IconCheck,
    status: 'live',
    action: ACTIONS.CC_VIEW,
    purpose: 'What to work today, out of your own book — ranked, with the reason each row is on the list. Opens the record in the Command Center.',
    audience: 'SR',
    reads: 'The same HubSpot championship feed as the Command Center, scoped to your book',
  },
  /* THE TRADE DESK — THIRD, BESIDE MY WORKLIST, AND NOT BURIED.
   *
   * Brian: *"What is a trade desk. It's a place where our team sees the
   * championship opportunities their coworkers are asking to trade."*
   *
   * ⚠️ IT SITS NEXT TO MY WORKLIST BECAUSE IT IS THE SAME KIND OF THING — a
   * rep's own daily screen — and a rep who cannot find it will not post an
   * offer, which makes the board empty for everyone else too. `/my-worklist`
   * shipped in 2.88.0 reachable only by typing the URL; one registry line was
   * the whole fix, and a board nobody can reach is a worse version of that
   * same defect because its value depends on other people using it.
   *
   * ⚠️ CC_VIEW, NOT AN EXEC GRANT, AND NOT A HAND-KEPT LIST OF REPS. Every
   * role holds CC_VIEW, and the whole point is that a rep sees what their
   * COLLEAGUES are offering. Gating it to exec would make it a manager's
   * conflict queue. Visibility here is navigation hygiene; the backend decides
   * whose records can actually move — a rep sees every offer and can only
   * claim into their own book, and the executive move is cc_rep_assign
   * server-side whether or not this link is drawn.
   *
   * ⚠️ AND IT CARRIES NO MONEY FIGURE, which is what makes that grant safe. An
   * executive move after a crown affects CREDIT; the desk names that on the
   * card and settles none of it. No trade-desk route is on MONEY_SURFACES. */
  {
    to: '/trade-desk',
    label: 'Trade Desk',
    icon: IconCheck,
    status: 'live',
    action: ACTIONS.CC_VIEW,
    purpose: 'What your coworkers are asking to trade — from a whole governing body down to one championship — with days to the event, who holds it now, and what has already been done on it. A trade completes before the championship happens or it takes an executive. Shows real coverage: owned versus owned-with-a-plan, and the gap.',
    audience: 'SR',
    reads: 'The trade ledger and offer board (sr-portal-backend) joined live onto the same HubSpot championship feed the Command Center draws',
  },
  {
    to: '/command-center',
    label: 'Command Center',
    icon: IconCommand,
    status: 'live',
    action: ACTIONS.CC_VIEW,
    purpose: 'Every opportunity we are chasing — championships, RFPs, corporate, cheer, police, fire and the rest. Who owns it and what the next step is.',
    audience: 'SR',
    reads: 'HubSpot championships (2-64425303) + the pursuit lines',
  },
  {
    to: INTERNAL_ROUTE,
    label: INTERNAL_LABEL,
    icon: IconJourney,
    status: 'live',
    action: ACTIONS.JOURNEY_VIEW,
    /* ⚠️ THE LABEL AND ROUTE COME FROM lib/surfaces.js, NOT FROM HERE.
     * This entry and the purpose strip on the page itself are the two places a
     * rep reads what this module is. When they were two hand-written strings
     * they drifted — which is the defect this whole release exists to fix, so
     * duplicating the name here would be an odd way to close it. */
    purpose: 'The floor’s view of the work — the 14 production stages, the internal queue, and what is late. Internal only; no customer sees it.',
    audience: 'SR',
    reads: 'ClickUp list 901000306889 (owns the production order) · HubSpot mirrors it',
  },
  // REVIEWS SITS HERE, ON ITS OWN, AND ABOVE PRICING ON PURPOSE.
  //
  // Brian: "reviews should have its own portal, but everything should be
  // interconnected and it shouldn't be associated with solely finance."
  // It is a daily workflow driven by DELIVERY — it belongs beside the journey
  // that ends in one, not tucked inside the money.
  {
    to: '/reviews',
    label: 'Reviews',
    icon: IconReviews,
    status: 'live',
    action: ACTIONS.REVIEWS_VIEW,
    purpose: 'Who to ask for a review today. Driven by DELIVERY — somebody has the ring in their hands — never by payment.',
    audience: 'SR',
    reads: 'ClickUp delivery + Shippo',
  },
  {
    to: '/pricing',
    label: 'Pricing Engine',
    icon: IconPricing,
    status: 'live',
    action: ACTIONS.PRICING_VIEW,
    purpose: 'Quoting. A separate application with its own sign-in — this is the door to it, not a copy of it.',
    audience: 'SR',
    reads: 'The pricing engine on Railway',
  },
  {
    to: '/artwork-studio',
    label: 'Artwork Studio',
    icon: IconArtwork,
    status: 'live',
    action: ACTIONS.ARTWORK_VIEW,
    purpose: 'Building the piece — real components onto a real housing, keyed type, owned crests. Nothing about the ring is AI-invented.',
    audience: 'SR',
    reads: 'Canva folders + HubSpot File Manager + the render engine',
  },
  {
    to: '/product-builders',
    label: 'Product Builders',
    icon: IconBuilders,
    status: 'beta',
    action: ACTIONS.BUILDERS_VIEW,
    purpose: 'The real SR products, each handing off into the right pricing line and artwork flow.',
    audience: 'SR',
    reads: 'The product registry',
  },
  // Launcher into the standalone customer video/asset portal. Sits with the
  // other folded-in tools because that is what it is — a link surface, not a
  // second copy of the portal.
  {
    to: '/presentations',
    label: 'Presentations',
    icon: IconPresent,
    status: 'live',
    action: ACTIONS.PRESENTATIONS_VIEW,
    purpose: 'The gated film and asset portal we send a customer to. This is the door; the portal owns the password and the viewing record.',
    audience: 'Customer',
    reads: 'present.srchamp.com',
  },
  // Launcher into the customer portal at team.srchamp.com. Sits beside
  // Presentations because it is the same kind of thing - the front door to a
  // standalone customer-facing app, not a second copy of it. It is deliberately
  // NOT embedded: that portal signs in people from outside SR, and this app
  // holds rep books and pricing.
  {
    to: '/team-rooms',
    label: EXTERNAL_LABEL,
    icon: IconPresent,
    status: 'live',
    action: ACTIONS.TEAMROOMS_VIEW,
    /* SR's ONE EXTERNAL SURFACE — the counterpart to Production, split by
     * customer-visible vs internal rather than by headcount. */
    purpose: 'SR’s one customer-facing surface — approvals and signatures, rosters and timestamps, the review ask, marketing material, delivery assets and the contract. Its own app with its own sign-in.',
    audience: 'Customer',
    reads: 'team.srchamp.com — Shopify team-store orders (who has bought) + what SR posts',
  },
  // Curated directory of SR's elite / priority accounts. Sits next to
  // Presentations because it is the same kind of surface — a front door into
  // customer-facing portals that own their own data — just organised by ACCOUNT
  // rather than by tool.
  {
    to: '/top-tier-teams',
    label: 'Top Tier Teams',
    icon: IconTopTier,
    status: 'live',
    action: ACTIONS.TOPTIER_VIEW,
    purpose: 'The elite accounts, organised by TEAM rather than by tool — every portal that account touches, in one card.',
    audience: 'SR',
    reads: 'The team registry',
  },
  // Launcher into SR's Custom GPTs. Same family as Presentations and Top Tier
  // Teams — a front door into an application that owns itself. A Custom GPT has
  // no embed API, so this could only ever be a link surface; see lib/aiTools.js
  // for why, and for the access caveat the page states out loud.
  {
    to: '/ai-tools',
    label: 'AI Tools',
    icon: IconSpark,
    status: 'live',
    action: ACTIONS.AITOOLS_VIEW,
    purpose: 'The Custom GPTs built for SR work \u2014 crowning a champion from a source, artwork briefs, bezel drops. Each is tuned for a job we repeat, so nobody starts from a blank chat.',
    audience: 'SR',
    reads: 'ChatGPT \u2014 they run there, with your own sign-in',
  },
  // Every state entity, on the board. The association is the AUTHORITY behind a
  // championship record — UIL decides Texas football has twelve divisions — so
  // this is the way into a state's work, not a directory of names.
  // ⚠️ No logo is hot-linked from an association's website: they are trademarks,
  // and an external URL is a dead link waiting to happen. markFor() refuses one.
  {
    to: '/governing-bodies',
    label: 'Governing Bodies',
    icon: IconTopTier,
    status: 'live',
    action: ACTIONS.CC_VIEW,
    purpose: 'The association that sanctions every high-school state championship we sell into \u2014 all 51, plus the second association some states run \u2014 with what we hold for that state beside it.',
    audience: 'SR',
    reads: 'The SR body registry + the Command Center championship feed',
  },
  // The counterpart to Governing Bodies, and the question that page structurally
  // cannot answer about itself: WHICH BODIES HAVE NO CARD. It derives its cards
  // from the registry and joins records onto them, so a body with records and no
  // registry entry is not shown as empty — it is absent. That is how the Pac-12
  // held six assigned championships and rendered nowhere.
  {
    to: '/body-coverage',
    label: 'Body Coverage',
    icon: IconCommand,
    status: 'live',
    action: ACTIONS.CC_VIEW,
    purpose: 'Every governing body named on a record, and whether the board can open it — with the four buckets reconciled against the whole object, so nothing can be silently dropped. No coverage percentage: the honest one has three different denominators.',
    audience: 'SR',
    reads: 'The SR body registry vs. sr_conference on the championship feed',
  },
  // THE DOOR. Four sets of findings that existed only as a CSV, an HTML file and
  // a scratch JSON in C:\Users\brian\.sr-scratch — which is to say, invisible,
  // which is to say nobody was going to work them.
  //
  // ⚠️ ONE ENTRY FOR FOUR SURFACES, AND THE TABS ARE REAL LINKS. The sidebar is
  // a tool, not an index: four entries for four views of the same question would
  // push Team Management off a phone screen. Each tab has its own URL
  // (/invisible-records/<surface>), so a single surface is still sendable — the
  // defect this page is named after is a finding nobody can reach, and a tab
  // that cannot be linked is a smaller version of the same thing.
  //
  // ⚠️ CC_VIEW, NOT SYSTEM_RUN. Every role holds CC_VIEW, and the reps are who
  // has to work these records. Gating it to exec would put the work list behind
  // the people who are not doing the work. It carries NO money figure, which is
  // what makes that grant safe — see the header comment in InvisibleRecords.jsx.
  {
    to: '/invisible-records',
    label: 'Invisible Records',
    icon: IconCommand,
    status: 'live',
    action: ACTIONS.CC_VIEW,
    purpose: 'The four sets nothing in the product could reach — bodies we have never recorded, records with no governing body and the reason each one is stuck, titles decided by standings rather than a final, and the registry rows that are asserted rather than sourced.',
    audience: 'SR',
    reads: 'The live championship feed + the dated SR body registry snapshot (read 2026-10-11)',
  },
  // The durable half of the Texas/UIL audit. Only 3 of 65 states were structurally
  // clean, so this is not a Texas problem and a one-off audit would not have kept
  // it fixed. Reads the SAME feed the board does, so it can never disagree with it.
  /* THE ONE NUMBER. Brian, 2026-10-18: "Yes it does bother me. We need to
   * get this clean and perfect." Nothing in the product answered "is the
   * universe clean?" — every number that existed answered a narrower
   * question and could read perfect while the object rotted. Sits above
   * Championship Integrity on purpose: that page checks records for defects
   * visible IN the records; this one counts what is invisible by
   * construction, which is the half that kept reading green. */
  {
    to: '/reconciliation',
    label: 'Reconciliation',
    icon: IconCommand,
    status: 'live',
    action: ACTIONS.CC_VIEW,
    purpose: 'One screen, read live off the object: governing bodies blind (no real date, so no clock can ever start), records with no body, no real date, no owner, no key, championships concluded with nobody crowned, and dates that call themselves Confirmed while their own source says +52wks. Unmeasured shows as an em dash, never as zero.',
    audience: 'SR',
    reads: 'GET /api/reconciliation — the live object, paged in full, cached 60s with its age published',
  },
  {
    to: '/championship-integrity',
    label: 'Championship Integrity',
    icon: IconCommand,
    status: 'live',
    action: ACTIONS.CC_VIEW,
    purpose: 'Every state, checked for the defects that make a championship record unusable \u2014 missing keys and dates, duplicates, and a class recorded beside its own divisions. Structurally clean is not the same as complete, and it says so.',
    audience: 'SR',
    reads: 'The same championship feed as the Command Center',
  },
  // The TAM, and specifically the RATIO. An organization with nobody attached
  // cannot be sold to, so every org count on this page is shown beside the
  // people on it — 291 police/fire/government departments held 28 people
  // between them when this was built, which an org count alone never showed.
  {
    to: '/tam',
    label: 'TAM',
    icon: IconTopTier,
    status: 'live',
    action: ACTIONS.CC_VIEW,
    purpose: 'Every state and every segment — how many organizations we hold, and how many people are actually attached to them. Zeros are shown, because a state holding nothing is the work.',
    audience: 'SR',
    reads: 'HubSpot companies + contacts',
  },
  // The door to the state directory loader. It existed as a working backend for
  // weeks with nothing in the product that could reach it — running a state
  // meant pasting a bearer token and a Drive file id into a browser console.
  // SYSTEM_RUN is exec + ops, so rep / marketing / unscoped never see it.
  {
    to: '/school-directory',
    label: 'School Directory',
    icon: IconBuilders,
    status: 'live',
    action: ACTIONS.SYSTEM_RUN,
    purpose: 'Load a state’s schools and the people who run them — coaches, athletic directors, principals — out of the Drive directories and into HubSpot. Fills empty fields only; never overwrites, and reports disagreements instead of resolving them.',
    audience: 'SR',
    reads: 'Google Drive “Coaches Dir” → HubSpot companies + contacts',
  },
  // Exec-only. Unlike the module entries above, this one genuinely disappears
  // for everyone else — TEAM_MANAGE is granted to `exec` alone, so `visibleNav`
  // drops it for rep / ops / marketing / unscoped.
  {
    to: '/team',
    label: 'Team Management',
    icon: IconTeam,
    status: 'live',
    action: ACTIONS.TEAM_MANAGE,
    purpose: 'Who is on the team, what role they hold, and what that role can do. Exec only.',
    audience: 'SR',
    reads: 'The SR user roster',
  },
  /* ⚠️ APPENDED, NOT INSERTED — the same rule the Command Center spaces follow.
   * A module that already has a rail position does not move, because people
   * navigate by muscle memory.
   *
   * ⭐ AND IT IS IN THE RAIL AT ALL BECAUSE THAT IS THE WHOLE DELIVERABLE.
   * `/my-worklist` shipped in 2.88.0 reachable only by typing the URL: the
   * screen built to tell a rep what to do today could not be found by the
   * people it was built for. A working endpoint with no entry here is not
   * shipped. One registry line is the whole fix.
   *
   * Reads NCAA.com, which is the only entry in this file that reads something
   * outside SR — stated rather than smoothed over, because `reads` exists so
   * two modules showing the same fact can be told apart by where it came from.
   * The bowl GAMES are a Command Center space reading our own championship
   * object; this is which TEAMS are heading for one, read from the governing
   * body's standings table. Different question, different source. */
  {
    to: '/bowl-eligibility',
    label: 'Bowl Eligibility',
    icon: IconCheck,
    status: 'live',
    action: ACTIONS.CC_VIEW,
    purpose: 'Which FBS teams have the six wins a bowl invitation needs, which still can, and which cannot — all 138, read once a week after the Saturday slate settles. A team whose sixth win was over an FCS opponent shows as contingent, because the NCAA does not publish the scholarship test that decides it.',
    audience: 'SR',
    reads: 'NCAA.com FBS standings, stored weekly by sr-fbs-bowl-eligibility',
  },
]

/**
 * The directory the Home page draws.
 *
 * Derived from NAV, so a module added to the sidebar appears here with no
 * second edit — and one added WITHOUT a purpose is visible as missing rather
 * than silently absent. Home itself is dropped: a card on the Home page
 * explaining the Home page is noise.
 */
export function systemDirectory(nav = NAV) {
  return nav
    .filter((n) => n.to !== '/')
    .map((n) => ({
      to: n.to,
      label: n.label,
      status: n.status,
      action: n.action,
      audience: n.audience || 'SR',
      reads: n.reads || '',
      /* Said out loud rather than left blank, so the gap is somebody's job. */
      purpose: n.purpose || 'No description yet — add `purpose` to this entry in nav.js.',
      described: Boolean(n.purpose),
    }))
}
